import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Row, Col, Spin, Alert, FloatButton } from 'antd';
import PropTypes from 'prop-types';
import { ExpandOutlined, MenuUnfoldOutlined, MenuFoldOutlined } from '@ant-design/icons';
import L from 'leaflet';
import { IconProvider, useIconSelection } from './iconContext.js';
import MapDisplay from './MapDisplay.js';
import MapLegend from './mapLegend.js';
import { useMapData } from './useMapData.js';
import { calculateModuleOverlaysAndBounds, createMarkerObject} from './mapLayoutService.js';
import { DEFAULT_ZOOM, SIDEBAR_WIDTH, DEFAULT_ICON_SCALE } from './mapConstants.js';
import { initializeIconsStructure, getCategoryAndSubcategory } from './mapIconUtils.js';
import 'leaflet/dist/leaflet.css';

const CATEGORY_PATTERNS = {
Shrines: ['Statue01', 'Statue02','Statue03','Statue04','Statue05','Id_Props_AltarOfSacrifice'],
'Extractions / Player Spawns': { Extractions: ['^Id_Extraction','BP_PedestalPortal','BP_DungeonEscape_C','BP_FixedStairEscape_C','BP_DungeonDown','BP_FixedStairDown','BP_CaveExit','BP_FixedElevator',], 'Player Spawns': ['DCPlayerStart'], },
Chests: { 'High Tier': ['Id_Props_Coffin_06','OrnateChest','ChestSpecial','MarvelousChest','Hoard01','Hoard',], 'Low Tier': ['Chest', 'Coffin', 'Id_Props_SkeletonBones', 'Id_Props_SkeletonCorpse','StoneTomb'], },
Resources: { Ores: ['_Ore'], Herbs: ['Flower', 'Wardweed', 'Lifeleaf'], },
Monsters: { Bosses: ['Lich','Warlord','GhostKing','FrostWyvern','Monster_CaveTroll','Monster_Cyclops','UndeadKnight','LavaMonster','SpectralKnight',], 'Mini Bosses': ['Wraith','Centipede','Yeti','IceHarpy','FrostGiantBerserker','FrostGiantShielder','Wendigo','FrostImp','FrostDemon','Centaur','Berserker','SkeletonChampion','Cockatrice','GiantWorm','RuinsGolem','Abomination','Dreadspine','Cerberus'], Mobs: ['Monster','Wisp','DireWolf','DeathBeetle','GiantDragonfly','GiantBat','DeathSkull','Ranged','Crossbowman','Archer','Bolas','Mage','SpiderPot','Id_Monster_Slime',], },
Miscellaneous: { Interactables: [' Door', 'Lever', 'Id_Props_StatueLever'], Traps: ['WallSpike', 'FloorSpikes', 'TrackAxe'], Keys: ['SkullKey'], Props: ['Id_Props_'], },
'Loot Spawns': ['LootDrop'], Modules: ['^MN_'],

};
export const GAME_MODES = {
ADVENTURE: { key: 'A', label: 'Adventure' },
NORMAL: { key: 'N', label: 'Normal' },
HR: { key: 'HR', label: 'High Roller' },
};

const MapMainContent = ({ selectedCharacter, characters, setAndStoreCharacters, mainNavCollapsed }) => {
const {
    availableMapTypes, isManifestLoading, manifestError,
    selectedMapType, focusedModuleName, setFocusedModuleName,
    gameMode, setGameMode,
    currentMapData,
    moduleDetailsForLegend,
    isLoadingMapData,
    mapDataError,
    lootData: currentGradeLootData,
    lootError: currentGradeLootError,
    ensureLootDataIsLoaded,
    lootDataByGrade,
    setSelectedMapType,
    itemSearchTerm, setItemSearchTerm, itemSearchResult, searchItems, clearItemSearch,
    isBuildingIndex, isSearchIndexReady,
} = useMapData();
const { selectedIconNames } = useIconSelection();

const [mapOverlays, setMapOverlays] = useState([]);
const [mapGridBounds, setMapGridBounds] = useState(null);
const [mapMarkers, setMapMarkers] = useState([]);
const [iconStates, setIconStates] = useState(() => initializeIconsStructure(CATEGORY_PATTERNS));
const [isLoadingOverlays, setIsLoadingOverlays] = useState(false);
const [isLoadingMarkers, setIsLoadingMarkers] = useState(false);
const [processedMarkerCount, setProcessedMarkerCount] = useState(0);

const [isCollapsed, setIsCollapsed] = useState(false);
const [zoom, setZoom] = useState(() => parseFloat(localStorage.getItem('zoom')) || DEFAULT_ZOOM);
const [isolatedModule, setIsolatedModule] = useState(() => localStorage.getItem('focusedModuleName') || null);
const [fitBoundsTrigger, setFitBoundsTrigger] = useState(0);
const [mapIconScale, setMapIconScale] = useState(() => parseInt(localStorage.getItem('mapIconScale')) || DEFAULT_ICON_SCALE);
const [moduleIconScale, setModuleIconScale] = useState(() => parseInt(localStorage.getItem('moduleIconScale')) || DEFAULT_ICON_SCALE);
const [legendScale, setLegendScale] = useState(() => parseInt(localStorage.getItem('legendScale')) || DEFAULT_ICON_SCALE);

const [showModuleNamesOnMap, setShowModuleNamesOnMap] = useState(() => {
    const stored = localStorage.getItem('showModuleNamesOnMap');
    return stored ? JSON.parse(stored) : false;
});

useEffect(() => { localStorage.setItem('zoom', zoom.toString()); }, [zoom]);
useEffect(() => { localStorage.setItem('mapIconScale', mapIconScale.toString()); }, [mapIconScale]);
useEffect(() => { localStorage.setItem('moduleIconScale', moduleIconScale.toString()); }, [moduleIconScale]);
useEffect(() => { localStorage.setItem('legendScale', legendScale.toString()); }, [legendScale]);
useEffect(() => {
    localStorage.setItem('showModuleNamesOnMap', JSON.stringify(showModuleNamesOnMap));
}, [showModuleNamesOnMap]);


useEffect(() => {
    if (isLoadingMapData || !currentMapData || Object.keys(currentMapData).length === 0) {
        setMapOverlays([]); setMapGridBounds(null); setIsLoadingOverlays(false); return;
    }
    const modulesWithDimensionsAndImages = Object.fromEntries(
        Object.entries(currentMapData).filter(([_, mod]) => mod && mod.dimensions && mod.imagePath)
    );
    if (Object.keys(modulesWithDimensionsAndImages).length === 0) {
        setMapOverlays([]); setMapGridBounds(null); setIsLoadingOverlays(false); return;
    }

    setIsLoadingOverlays(true);
    const timer = setTimeout(() => {
        try {
            const { overlays, gridBounds } = calculateModuleOverlaysAndBounds(modulesWithDimensionsAndImages, selectedMapType);
            setMapOverlays(overlays); setMapGridBounds(gridBounds);
        } catch (error) { setMapOverlays([]); setMapGridBounds(null); console.error("Error calculating overlays:", error); }
        finally { setIsLoadingOverlays(false); }
    }, 0);
    return () => clearTimeout(timer);
}, [currentMapData, selectedMapType, isLoadingMapData]);


useEffect(() => {
    if (focusedModuleName && mapOverlays.length > 0 && !mapOverlays.some(ov => ov.moduleName === focusedModuleName)) {
        setFocusedModuleName(null); setIsolatedModule(null);
    }
    if (focusedModuleName !== isolatedModule) setIsolatedModule(focusedModuleName);
}, [focusedModuleName, mapOverlays, setFocusedModuleName, isolatedModule]);

useEffect(() => {
    const isAnyModuleActorDataLoading = Object.values(currentMapData).some(mod => mod && mod.isLoadingActors);

    if (isLoadingMapData || isAnyModuleActorDataLoading || isLoadingOverlays || !currentMapData || Object.keys(currentMapData).length === 0 || mapOverlays.length === 0 ) {
        setMapMarkers([]); setIconStates(initializeIconsStructure(CATEGORY_PATTERNS));
        setProcessedMarkerCount(0); setIsLoadingMarkers(false); return;
    }
    setIsLoadingMarkers(true);
    setMapMarkers([]);
    setIconStates(initializeIconsStructure(CATEGORY_PATTERNS));
    setProcessedMarkerCount(0);

    let actorQueue = [];
    Object.entries(currentMapData).forEach(([moduleName, moduleContent]) => {
        if (moduleContent && !moduleContent.isLoadingActors && !moduleContent.errorLoadingActors && moduleContent.data && Array.isArray(moduleContent.data)) {
            const overlayInfo = mapOverlays.find(ov => ov.moduleName === moduleName);
            if (overlayInfo && moduleContent.dimensions) {
                moduleContent.data.forEach((actorData, actorIndex) => {
                    actorQueue.push({ actorData, actorIndex, moduleName, moduleGridBounds: overlayInfo.bounds, tileDimensions: moduleContent.dimensions, moduleLocalizedName: moduleContent.Module_LocalizedString });
                });
            }
        }
    });

    if (actorQueue.length === 0) { setIsLoadingMarkers(false); return; }

    let currentIndex = 0; const tempIconStates = initializeIconsStructure(CATEGORY_PATTERNS);
    let animationFrameId;

    function processBatch() {
        const batchEndTime = performance.now() + 16;
        const currentBatchMarkers = [];
        const currentScale = !isNaN(mapIconScale) && mapIconScale > 0 ? mapIconScale : DEFAULT_ICON_SCALE;

        while (currentIndex < actorQueue.length && performance.now() < batchEndTime) {
            const { actorData, actorIndex, moduleName, moduleGridBounds, tileDimensions, moduleLocalizedName } = actorQueue[currentIndex];
            currentIndex++;
            if (!actorData?.object_name || !actorData?.object_location) continue;

            const moduleInfoForMarker = { moduleName, moduleDisplayName: moduleLocalizedName, gridBounds: moduleGridBounds, dimensions: tileDimensions };
            const marker = createMarkerObject(moduleInfoForMarker, actorData, actorIndex, selectedMapType, gameMode, currentScale, actorData.LocalizedString);

            if (marker) {
    // Call getCategoryAndSubcategory first and store the result
    const categoryInfo = getCategoryAndSubcategory(actorData.object_name, actorData.type, CATEGORY_PATTERNS);

    // Check if the item was excluded (i.e., categoryInfo is null)
    if (categoryInfo !== null) {
        // If not null, it's safe to destructure and proceed
        currentBatchMarkers.push(marker); // Add marker only if item is not excluded
        const { category, subcategory, displayName } = categoryInfo; // Assuming displayName is returned as per my last suggestion
                                                               // If not, just use category and subcategory

        const entryForIconStates = { ...actorData, marker: marker, displayName: displayName || actorData.object_name }; // Include a display name

        try {
            const targetCat = tempIconStates[category];

            // Your existing categorization logic using 'category' and 'subcategory'
            if (category && subcategory && targetCat?.[subcategory] && Array.isArray(targetCat[subcategory])) {
                targetCat[subcategory].push(entryForIconStates);
            } else if (category && !subcategory && Array.isArray(targetCat)) {
                targetCat.push(entryForIconStates);
            } else if (category && !subcategory && targetCat && typeof targetCat === 'object' && !Array.isArray(targetCat)) {
                // This part handles cases where a category is defined as an object (expecting subcategories)
                // but the item itself doesn't have a subcategory.
                // You might want to decide how to handle this.
                // Option 1: Put it in a default subcategory if one exists (e.g., the first one)
                const defaultSub = Object.keys(targetCat)[0];
                if (defaultSub && targetCat[defaultSub] && Array.isArray(targetCat[defaultSub])) {
                    targetCat[defaultSub].push(entryForIconStates);
                } else {
                    // Fallback if category structure is unexpected or no valid subcategory array found
                    if (!tempIconStates['Uncategorized']) tempIconStates['Uncategorized'] = [];
                    tempIconStates['Uncategorized'].push(entryForIconStates);
                }
            } else {
                // Default fallback to Uncategorized
                if (!tempIconStates['Uncategorized']) tempIconStates['Uncategorized'] = [];
                tempIconStates['Uncategorized'].push(entryForIconStates);
            }
        } catch (e) {
            // Catch-all for any errors during categorization, place in Uncategorized
            console.error("Error during icon state categorization:", e, "for item:", entryForIconStates);
            if (!tempIconStates['Uncategorized']) tempIconStates['Uncategorized'] = [];
            tempIconStates['Uncategorized'].push(entryForIconStates);
        }
    } else {
        // categoryInfo is null, meaning the item was excluded by getCategoryAndSubcategory
        // console.log(`Item "${actorData.object_name}" excluded, not adding to icon states or markers.`);
        // Do not push the marker to currentBatchMarkers here if the item is fully excluded.
        // If you still need the marker for some other reason even if the item isn't categorized for UI,
        // you'd move `currentBatchMarkers.push(marker);` outside this `if (categoryInfo !== null)` block,
        // but typically if an item is excluded from categorization, its marker might also be irrelevant.
    }
}
        }
        if (currentBatchMarkers.length > 0) setMapMarkers(prev => [...prev, ...currentBatchMarkers]);
        setProcessedMarkerCount(currentIndex);

        if (currentIndex < actorQueue.length) {
            animationFrameId = requestAnimationFrame(processBatch);
        } else {
            setIconStates(tempIconStates);
            setIsLoadingMarkers(false);
        }
    }
    animationFrameId = requestAnimationFrame(processBatch);
    return () => { cancelAnimationFrame(animationFrameId); setIsLoadingMarkers(false); };
}, [currentMapData, mapOverlays, isLoadingMapData, isLoadingOverlays, mapIconScale, selectedMapType, gameMode]);

const legendIconStatesToShow = useMemo(() => {
    if (Object.keys(iconStates).length === 0 || !iconStates) return initializeIconsStructure(CATEGORY_PATTERNS);
    if (!focusedModuleName) return iconStates;
    const moduleSpecificIconStates = initializeIconsStructure(CATEGORY_PATTERNS); let hasContent = false;
    Object.entries(iconStates).forEach(([catKey, catValue]) => {
        if (Array.isArray(catValue)) {
            const itemsInModule = catValue.filter(item => item.marker?.moduleName === focusedModuleName);
            if (itemsInModule.length > 0) { moduleSpecificIconStates[catKey] = itemsInModule; hasContent = true; }
        } else if (typeof catValue === 'object' && catValue !== null) {
            const subCatObj = {}; let subContent = false;
            Object.entries(catValue).forEach(([subKey, subValueArray]) => {
                if (Array.isArray(subValueArray)) {
                    const itemsInModuleSub = subValueArray.filter(item => item.marker?.moduleName === focusedModuleName);
                    if (itemsInModuleSub.length > 0) { subCatObj[subKey] = itemsInModuleSub; subContent = true; }
                }
            });
            if (subContent) { moduleSpecificIconStates[catKey] = subCatObj; hasContent = true; }
        }
    });
    return hasContent ? moduleSpecificIconStates : initializeIconsStructure(CATEGORY_PATTERNS);
}, [focusedModuleName, iconStates]);

const { filteredOverlays, filteredMarkersToPlot } = useMemo(() => {
    const baseMarkers = mapMarkers || []; const baseOverlays = mapOverlays || [];
    let searchFilteredModuleNames = null;
    if (itemSearchTerm && itemSearchResult.searchPerformed) searchFilteredModuleNames = itemSearchResult.filteredModuleNames || new Set();
    let overlaysToUse = baseOverlays; let markersPreSelection = baseMarkers;
    if (searchFilteredModuleNames !== null) {
        overlaysToUse = baseOverlays.filter(ov => searchFilteredModuleNames.has(ov.moduleName));
        const sourcesFound = itemSearchResult.foundSources;
        markersPreSelection = baseMarkers.filter(marker => searchFilteredModuleNames.has(marker.moduleName) && sourcesFound.has(marker.object_name));
    }
    const selectionFilteredMarkers = markersPreSelection.filter(marker => marker.object_name && selectedIconNames.has(marker.object_name));
    return { filteredOverlays: overlaysToUse, filteredMarkersToPlot: selectionFilteredMarkers };
}, [mapMarkers, mapOverlays, selectedIconNames, itemSearchTerm, itemSearchResult]);

const handleMapZoomChange = useCallback((newZoom) => { setZoom(Math.max(-10, Math.min(10, newZoom))); }, []);
const handleSliderZoomChange = useCallback((newZoom) => { if (typeof newZoom === 'number' && !isNaN(newZoom)) setZoom(Math.max(-10, Math.min(10, newZoom))); }, []);
const resetMapView = useCallback(() => { setFocusedModuleName(null); setIsolatedModule(null); clearItemSearch(); setFitBoundsTrigger(prev => prev + 1); }, [setFocusedModuleName, clearItemSearch]);
const focusOnModule = useCallback((moduleNameToFocus) => {
    if (focusedModuleName === moduleNameToFocus) { setFocusedModuleName(null); setIsolatedModule(null); }
    else { setFocusedModuleName(moduleNameToFocus); setIsolatedModule(moduleNameToFocus); }
    clearItemSearch(); setFitBoundsTrigger(prev => prev + 1);
}, [focusedModuleName, setFocusedModuleName, clearItemSearch]);
const handleSetSelectedMapTypeCallback = useCallback((mapType) => {
    setSelectedMapType(mapType); setFocusedModuleName(null); setIsolatedModule(null); setFitBoundsTrigger(prev => prev + 1);
}, [setSelectedMapType, setFocusedModuleName]);

const isAnyModuleActorDataLoading = useMemo(() => {
    return Object.values(currentMapData).some(mod => mod && mod.isLoadingActors);
}, [currentMapData]);

const displayOverallSpinner = isManifestLoading || isLoadingMapData || isLoadingOverlays || (Object.keys(currentMapData).length > 0 && isAnyModuleActorDataLoading);

const showMapShells = !isManifestLoading && !!selectedMapType && !isLoadingMapData && !isLoadingOverlays && mapOverlays?.length > 0 && !isAnyModuleActorDataLoading;
const criticalErrorForAlert = manifestError || mapDataError;

let loadingTip = "Loading...";
if (isManifestLoading) loadingTip = "Loading map list...";
else if (isLoadingMapData && Object.keys(currentMapData).length === 0) loadingTip = `Loading ${selectedMapType || 'map'} metadata...`;
else if (isLoadingMapData && isAnyModuleActorDataLoading) loadingTip = `Loading ${selectedMapType || 'map'} actor data...`;
else if (isLoadingOverlays) loadingTip = "Calculating map grid...";
else if (isLoadingMarkers) {
    const totalActorsEstimate = Object.values(currentMapData || {}).reduce((sum, mod) => {
        if (mod && !mod.isLoadingActors && mod.data) return sum + (mod.data.length || 0);
        return sum;
    }, 0);
    loadingTip = totalActorsEstimate > 0 ? `Placing markers... (${processedMarkerCount}/${totalActorsEstimate})` : `Placing markers... (${processedMarkerCount} processed)`;
}
else if (isBuildingIndex && !displayOverallSpinner) loadingTip = "Building item search index...";


const noModulesFoundForSelectedMap = useMemo(() => (
    selectedMapType && !isLoadingMapData && !mapDataError && moduleDetailsForLegend.length === 0 && Object.keys(currentMapData).length === 0 && !isAnyModuleActorDataLoading
), [selectedMapType, isLoadingMapData, mapDataError, moduleDetailsForLegend, currentMapData, isAnyModuleActorDataLoading]);

const currentMapKey = useMemo(() => `${selectedMapType}-${gameMode}`, [selectedMapType, gameMode]);
const plotUpdateKey = useMemo(() => `${isolatedModule || 'all'}-${(filteredMarkersToPlot || []).length}-${mapIconScale}-${itemSearchTerm}-${gameMode}-${(itemSearchResult.filteredModuleNames?.size || 0)}`, [filteredMarkersToPlot, mapIconScale, itemSearchTerm, gameMode, itemSearchResult.filteredModuleNames, isolatedModule]);
const currentMapIconScaleToUse = !isNaN(mapIconScale) && mapIconScale > 0 ? mapIconScale : DEFAULT_ICON_SCALE;

return (
    <div style={{ height: '100vh', width: '100%', display: 'flex' }}>
        <Col style={{ height: '100%', flexGrow: 1, overflow: 'hidden', position: 'relative', background: '#1E1C18' }}>
            {displayOverallSpinner && (<div style={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'rgba(0,0,0,0.7)', zIndex: 1100 }}> <Spin size="large" tip={loadingTip} /> </div>)}

            {showMapShells && !noModulesFoundForSelectedMap ? (
                <MapDisplay
                    mapKey={currentMapKey}
                    moduleOverlays={filteredOverlays}
                    iconsToPlot={filteredMarkersToPlot}
                    initialBounds={mapGridBounds}
                    isolatedModule={isolatedModule}
                    zoom={zoom} setZoom={handleMapZoomChange}
                    updateVersion={fitBoundsTrigger}
                    plotUpdateKey={plotUpdateKey} 
                    mapIconScale={currentMapIconScaleToUse} 
                    lootDataByGrade={lootDataByGrade}
                    ensureLootDataIsLoaded={ensureLootDataIsLoaded}
                    mainNavCollapsed={mainNavCollapsed}
                    showModuleNamesOnMap={showModuleNamesOnMap}
                />
            ) : !displayOverallSpinner ? (
                 <div style={{ width: '100%', height: '100%', background: '#1E1C18', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#aaa', padding: '20px', textAlign: 'center' }}>
                    <span>
                        {isManifestLoading ? 'Initializing map list...'
                            : manifestError ? `Error loading map list. Please check connection or configuration.`
                            : !selectedMapType ? 'Select a Map Type from the sidebar.'
                            : mapDataError ? `Error loading data for "${selectedMapType}". ${mapDataError}`
                            : currentGradeLootError ? `Error loading loot data for current grade: ${currentGradeLootError}`
                            : noModulesFoundForSelectedMap ? `No modules found for the map "${selectedMapType}". The map data might be empty or misconfigured.`
                            : 'Map is currently unavailable. Please select a map or wait for loading to complete.'
                        }
                    </span>
                </div>
            ): null }

            {!displayOverallSpinner && criticalErrorForAlert && typeof criticalErrorForAlert === 'string' && (
                <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', width: 'auto', maxWidth: '90%', zIndex: 1100 }}>
                     <Alert message={"Error"} description={criticalErrorForAlert} type={"error"} showIcon closable />
                </div>
            )}
            {!displayOverallSpinner && !criticalErrorForAlert && itemSearchResult.searchPerformed && itemSearchResult.error && typeof itemSearchResult.error === 'string' && (
                 <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', width: 'auto', maxWidth: '90%', zIndex: 1100 }}>
                    <Alert message={"Search Info"} description={itemSearchResult.error} type={"warning"} showIcon closable />
                </div>
            )}

            <FloatButton.Group shape="circle" style={{ position: 'absolute', bottom: 46, right: isCollapsed ? 15 : 15, zIndex: 1001, transition: 'right 0.3s ease-in-out' }}>
                <FloatButton tooltip={<div>Toggle Sidebar</div>} icon={isCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} onClick={() => setIsCollapsed(!isCollapsed)} />
                <FloatButton tooltip={<div>Reset View / Clear Search</div>} icon={<ExpandOutlined />} onClick={resetMapView} disabled={displayOverallSpinner} />
            </FloatButton.Group>
        </Col>
        <Col flex={isCollapsed ? '0 0 0px' : `0 0 ${SIDEBAR_WIDTH}px`} style={{ height: '100%', overflow: 'hidden', background: '#31363F', transition: 'flex-basis 0.3s ease-in-out', boxShadow: isCollapsed ? 'none' : '-2px 0 5px rgba(0,0,0,0.3)', zIndex: 1000, display: 'flex', flexDirection: 'column' }} >
            {!isCollapsed && (
                <div className="sidebar-scroll-container" style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
                    {isManifestLoading ? ( <div style={{ padding: '20px', textAlign: 'center', color: '#eee', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}> <Spin tip="Loading Map List..." /> </div>
                    ) : manifestError ? ( <div style={{ padding: '20px', textAlign: 'center', color: '#ff7875' }}> Error loading map list. Please check connection or configuration. </div>
                    ) : Object.keys(availableMapTypes).length === 0 ? ( <div style={{ padding: '20px', textAlign: 'center', color: '#aaa' }}> No map types found in the manifest. </div>
                    ) : (
                        <MapLegend
                            availableMapTypes={availableMapTypes} selectedMapType={selectedMapType} setSelectedMapType={handleSetSelectedMapTypeCallback}
                            focusedModuleName={focusedModuleName} focusOnModule={focusOnModule} gameMode={gameMode} setGameMode={setGameMode}
                            iconStates={legendIconStatesToShow} mapIconScale={mapIconScale} setMapIconScale={setMapIconScale}
                            moduleIconScale={moduleIconScale} setModuleIconScale={setModuleIconScale} legendScale={legendScale} setLegendScale={setLegendScale}
                            zoom={zoom} setZoom={handleSliderZoomChange} selectedCharacter={selectedCharacter} characters={characters} setAndStoreCharacters={setAndStoreCharacters}
                            isLoadingMapData={displayOverallSpinner} itemSearchTerm={itemSearchTerm} setItemSearchTerm={setItemSearchTerm}
                            itemSearchResult={itemSearchResult} searchItems={searchItems} clearItemSearch={clearItemSearch}
                            isBuildingIndex={isBuildingIndex} isSearchIndexReady={isSearchIndexReady} moduleDetailsForLegend={moduleDetailsForLegend}
                            showModuleNamesOnMap={showModuleNamesOnMap}
                            setShowModuleNamesOnMap={setShowModuleNamesOnMap}
                        />
                    )}
                </div>
            )}
        </Col>
    </div>
);
};

MapMainContent.propTypes = {
    selectedCharacter: PropTypes.string,
    characters: PropTypes.array,
    setAndStoreCharacters: PropTypes.func,
    mainNavCollapsed: PropTypes.bool,
};

MapMainContent.defaultProps = {
    selectedCharacter: null,
    characters: [],
    setAndStoreCharacters: () => {},
    mainNavCollapsed: false,
};

const MapMainWrapper = (props) => (
<IconProvider>
<MapMainContent {...props} />
</IconProvider>
);

export default MapMainWrapper;